import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

interface TeamStats {
  teamId: string;
  activeGuards: number;
  pendingReports: number;
  unreadMessages: number;
  activeShifts: number;
  lastActivity?: string;
}

export const useRealTimeStats = (teamIds: string[]) => {
  const [stats, setStats] = useState<Record<string, TeamStats>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!teamIds.length) {
      setIsLoading(false);
      return;
    }

    // Fetch initial stats
    const fetchStats = async () => {
      try {
        const teamStats: Record<string, TeamStats> = {};

        for (const teamId of teamIds) {
          // Get active clock-ins (guards currently on duty)
          const { data: activeClockIns } = await supabase
            .from('clock_in_out')
            .select('user_id')
            .eq('team_id', teamId)
            .eq('status', 'active');

          // Get pending reports count
          const { data: pendingReports } = await supabase
            .from('reports')
            .select('id')
            .eq('team_id', teamId)
            .eq('status', 'pending');

          // Get unread messages count (simplified - would need proper implementation)
          const { data: recentMessages } = await supabase
            .from('team_messages')
            .select('id')
            .eq('team_id', teamId)
            .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());

          // Get active shifts for today
          const today = new Date();
          const startOfDay = new Date(today.setHours(0, 0, 0, 0)).toISOString();
          const endOfDay = new Date(today.setHours(23, 59, 59, 999)).toISOString();

          const { data: activeShifts } = await supabase
            .from('team_schedules')
            .select('id')
            .eq('team_id', teamId)
            .gte('start_date', startOfDay)
            .lte('start_date', endOfDay);

          teamStats[teamId] = {
            teamId,
            activeGuards: activeClockIns?.length || 0,
            pendingReports: pendingReports?.length || 0,
            unreadMessages: recentMessages?.length || 0,
            activeShifts: activeShifts?.length || 0,
            lastActivity: new Date().toISOString(),
          };
        }

        setStats(teamStats);
      } catch (error) {
        console.error('Error fetching team stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();

    // Set up real-time subscriptions
    const subscriptions = teamIds.map(teamId => {
      return supabase
        .channel(`team-stats-${teamId}`)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'clock_in_out',
          filter: `team_id=eq.${teamId}`,
        }, () => {
          // Refresh stats when clock in/out changes
          fetchStats();
        })
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'team_messages',
          filter: `team_id=eq.${teamId}`,
        }, () => {
          // Refresh stats when messages change
          fetchStats();
        })
        .subscribe();
    });

    return () => {
      subscriptions.forEach(sub => {
        supabase.removeChannel(sub);
      });
    };
  }, [teamIds]);

  return { stats, isLoading };
};